<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Chiffre d’affaires & marges</h1>
          <small class="text-muted">Rentabilité des produits vendus sur la période sélectionnée.</small>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>

      <div class="card module-filter-card mb-3" data-tour="revenue-margin-filters">
        <div class="card-body py-2">
          <form class="row g-2 align-items-end" @submit.prevent="fetchReport">
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date du</label>
              <input v-model="filters.date_from" type="date" class="form-control form-control-sm" required />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date au</label>
              <input v-model="filters.date_to" type="date" class="form-control form-control-sm" required />
            </div>
            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" :disabled="isLoading" @click="resetFilters">
                <i class="fa fa-rotate-left" aria-hidden="true"></i><span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="submit" class="btn btn-primary btn-sm btn-icon-label" :disabled="isLoading">
                <i class="fa fa-search" aria-hidden="true"></i><span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="row g-3 mb-3" data-tour="revenue-margin-summary">
        <div v-for="card in summaryCards" :key="card.label" class="col-sm-6 col-xl">
          <div class="card border-0 shadow-sm h-100"><div class="card-body py-2">
            <div class="small text-muted">{{ card.label }}</div>
            <div class="fs-5 fw-bold" :class="card.className">{{ card.value }}</div>
          </div></div>
        </div>
      </div>

      <BootstrapDataTable
        state-key="reports.revenue-margins"
        :show-print="false"
        :rows="products"
        :columns="columns"
        :loading="isLoading"
        client-pagination
        :per-page="10"
        sort-by="revenue"
        sort-dir="desc"
        empty-text="Aucun produit vendu sur cette période."
        @refresh="fetchReport"
      >
        <template #cell-quantity="{ row }">{{ formatNumber(row.quantity || 0) }}</template>
        <template #cell-revenue="{ row }">{{ $format.currency(row.revenue || 0) }}</template>
        <template #cell-estimated_cost="{ row }">{{ $format.currency(row.estimated_cost || 0) }}</template>
        <template #cell-margin="{ row }"><span :class="marginClass(row.margin)">{{ $format.currency(row.margin || 0) }}</span></template>
        <template #cell-margin_rate="{ row }">{{ percentage(row.margin_rate) }}</template>
        <template #cell-revenue_share="{ row }">{{ percentage(row.revenue_share) }}</template>
        <template #cell-margin_to_total_revenue="{ row }">{{ percentage(row.margin_to_total_revenue) }}</template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { defaultReportDateRange } from '@/utils/reportDates';

export default {
  name: 'RevenueMarginsReportView',
  data() {
    return {
      filters: defaultReportDateRange(),
      summary: { revenue: 0, estimated_cost: 0, margin: 0, margin_rate: 0, products_count: 0 },
      products: [],
      isLoading: false,
      errorMessage: '',
      columns: [
        { key: 'product_name', label: 'Produit', sortable: true },
        { key: 'sku', label: 'SKU', sortable: true },
        { key: 'quantity', label: 'Qté nette', sortable: true, class: 'text-end' },
        { key: 'revenue', label: 'Chiffre d’affaires', sortable: true, class: 'text-end' },
        { key: 'estimated_cost', label: 'Coût estimé', sortable: true, class: 'text-end' },
        { key: 'margin', label: 'Marge', sortable: true, class: 'text-end' },
        { key: 'margin_rate', label: 'Taux de marge', sortable: true, class: 'text-end' },
        { key: 'revenue_share', label: 'Part du CA', sortable: true, class: 'text-end' },
        { key: 'margin_to_total_revenue', label: 'Marge / CA total', sortable: true, class: 'text-end' },
      ],
    };
  },
  computed: {
    summaryCards() {
      return [
        { label: 'Chiffre d’affaires', value: this.$format.currency(this.summary.revenue || 0) },
        { label: 'Coût estimé', value: this.$format.currency(this.summary.estimated_cost || 0) },
        { label: 'Marge brute', value: this.$format.currency(this.summary.margin || 0), className: this.marginClass(this.summary.margin) },
        { label: 'Taux de marge', value: this.percentage(this.summary.margin_rate) },
        { label: 'Produits vendus', value: this.formatNumber(this.summary.products_count || 0) },
      ];
    },
  },
  mounted() { this.fetchReport(); },
  methods: {
    async fetchReport() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const response = await this.$api.get('/reports/revenue-margins', { params: this.filters });
        const data = response.data?.data || response.data || {};
        this.summary = data.summary || this.summary;
        this.products = data.products || [];
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Impossible de charger le rapport de marge.';
      } finally {
        this.isLoading = false;
      }
    },
    resetFilters() { this.filters = defaultReportDateRange(); this.fetchReport(); },
    percentage(value) { return `${Number(value || 0).toFixed(2)} %`; },
    formatNumber(value) {
      const number = Number(value || 0);
      return new Intl.NumberFormat('fr-FR').format(Number.isFinite(number) ? number : 0);
    },
    marginClass(value) { const number = Number(value || 0); return number > 0 ? 'text-success' : (number < 0 ? 'text-danger' : 'text-muted'); },
  },
};
</script>
